import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Modal,
  notification,
  Form,
  message,
} from "lib/ui";
import { PlusOutlined, DeleteOutlined } from "lib/icons";
import {
  Mail,
  Link2,
  Tag,
  Layers,
  Pencil,
  Trash2,
  ExternalLink,
} from "lucide-react";
import AddUserForm from "./Add";
import fetchApi from "../../../hooks/ApiFetch";
import {
  AdminPageLayout,
  AdminHero,
  AdminStats,
  AdminSearchBar,
} from "../../../components/admin/AdminPageShell";
import {
  PeopleDataTable,
  ActionBtn,
} from "../../../components/admin/PeopleDataTable";

const ContactUsPage = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetchApi.get("/contactus");
      setData(Array.isArray(response.data) ? response.data : []);
    } catch {
      message.error("Failed to load contact entries");
    }
  };

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (row) => {
    setEditingUser(row);
    form.setFieldsValue(row);
    setIsModalVisible(true);
  };

  const handleDelete = (id, title) => {
    Modal.confirm({
      title: "Delete contact entry?",
      icon: <DeleteOutlined />,
      content: title ? `Remove ${title}` : undefined,
      okText: "Delete",
      cancelText: "Cancel",
      okType: "danger",
      onOk: async () => {
        try {
          await fetchApi.delete(`/contactus/${id}`);
          setData((prev) => prev.filter((item) => item._id !== id));
          notification.success({
            message: "Deleted",
            description: "Entry removed.",
          });
        } catch {
          notification.error({
            message: "Failed",
            description: "Could not delete entry.",
          });
        }
      },
    });
  };

  const handleSave = async (values) => {
    try {
      if (editingUser) {
        await fetchApi.put(`/contactus/${editingUser._id}`, values);
        setData((prev) =>
          prev.map((item) =>
            item._id === editingUser._id ? { ...item, ...values } : item
          )
        );
        notification.success({
          message: "Updated",
          description: "Contact entry saved.",
        });
      } else {
        const response = await fetchApi.post("/contactus", values);
        setData((prev) => [
          ...prev,
          { ...values, _id: response.data._id ?? response.data?._id },
        ]);
        notification.success({
          message: "Added",
          description: "Contact entry created.",
        });
      }
      setIsModalVisible(false);
      form.resetFields();
    } catch {
      notification.error({
        message: "Failed",
        description: "Could not save entry.",
      });
    }
  };

  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((item) =>
      ["icon", "details", "label", "link"].some((k) =>
        String(item[k] ?? "")
          .toLowerCase()
          .includes(q)
      )
    );
  }, [data, search]);

  const withLinks = data.filter((d) => d.link).length;

  const columns = [
    {
      key: "label",
      header: "Label",
      render: (row) => (
        <span className="font-semibold text-slate-900">{row.label || "—"}</span>
      ),
    },
    {
      key: "icon",
      header: "Icon",
      render: (row) => (
        <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
          {row.icon || "—"}
        </span>
      ),
    },
    {
      key: "details",
      header: "Details",
      render: (row) => (
        <span className="line-clamp-2 max-w-xs text-sm text-slate-600">
          {row.details || "—"}
        </span>
      ),
    },
    {
      key: "link",
      header: "Link",
      render: (row) =>
        row.link ? (
          <a
            href={row.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex max-w-[200px] items-center gap-1 truncate text-xs font-semibold text-brand hover:underline"
          >
            {row.link}
            <ExternalLink size={12} />
          </a>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
  ];

  return (
    <AdminPageLayout>
      <AdminHero
        icon={Mail}
        badge="Customer contact"
        title="Contact us"
        description="Contact channels and links displayed in the customer app."
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            className="!border-0 !bg-white !text-brand-dark hover:!bg-violet-50"
          >
            Add entry
          </Button>
        }
      />

      <AdminStats
        items={[
          { label: "Entries", value: data.length, icon: Mail },
          { label: "Showing", value: filteredData.length, icon: Layers },
          { label: "With links", value: withLinks, icon: Link2 },
          { label: "With labels", value: data.filter((d) => d.label).length, icon: Tag },
        ]}
      />

      <AdminSearchBar
        placeholder="Search icon, label, details, link…"
        value={search}
        onChange={setSearch}
      />

      <PeopleDataTable
        columns={columns}
        rows={filteredData}
        rowKey="_id"
        renderActions={(row) => (
          <>
            <ActionBtn variant="edit" onClick={() => handleEdit(row)}>
              <Pencil size={14} />
              Edit
            </ActionBtn>
            <ActionBtn
              variant="delete"
              onClick={() => handleDelete(row._id, row.label)}
            >
              <Trash2 size={14} />
              Delete
            </ActionBtn>
          </>
        )}
        emptyTitle="No contact entries"
        emptyHint="Add an entry or clear your search."
      />

      <Modal
        title={editingUser ? "Edit contact entry" : "Add contact entry"}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
      >
        <AddUserForm
          form={form}
          onFinish={handleSave}
          initialValues={editingUser || {}}
        />
      </Modal>
    </AdminPageLayout>
  );
};

export default ContactUsPage;
